import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import axios from 'axios';
import { MultiGrid, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized';

import { buildVisibleRows } from './assignTableRowModel';
import FloatInput from './FloatInput';
import VenueTooltip from './VenueTooltip';

import { formatAndRoundMoney, formatFigure, formatSeparateByThousands } from 'Helpers/formatService';

import CheckboxInput from 'CommonComponents/CheckboxInput';
import { HelpCircleIcon } from 'CommonComponents/icons';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import ArrowPathIcon from 'Assets/icons/arrow-path.svg';
import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronUpIcon from 'Assets/icons/chevron-up.svg';
import TrashIcon from 'Assets/icons/trash.svg';

// Column geometry. The original markup used tw-min-w-96 (24rem ≈ 384px) for both the
// fixed left column and each venue column.
const COLUMN_WIDTH = 384;
// The header row (rowIndex 0) and every expense body row are measured by CellMeasurer, so
// their heights are exact and need no constant. Divider rows stay a uniform fixed strip.
const DIVIDER_ROW_HEIGHT = 40; // charcoal group bar (drawn by the sticky overlay)
// Seed height for cells CellMeasurer has not measured yet. react-virtualized derives a row's
// height as the MAX of getHeight() across ALL columns, so every off-screen (unmeasured) column
// of a row contributes THIS value to that max — it must stay BELOW the shortest real row or
// short rows inflate. The measured venue cell (taller) always dominates, so the only cost of a
// low seed is a brief first-paint reflow as exact heights land. minHeight floors tiny cells.
const DEFAULT_MEASURED_HEIGHT = 80;
const MIN_MEASURED_HEIGHT = 40;

// prettier-ignore
const linkSvg = (
    <svg className="tw-w-4 tw-h-4 tw-shrink-0" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path fillRule="evenodd" d="M12.586 4.586a2 2 0 112.828 2.828l-3 3a2 2 0 01-2.828 0 1 1 0 00-1.414 1.414 4 4 0 005.656 0l3-3a4 4 0 00-5.656-5.656l-1.5 1.5a1 1 0 101.414 1.414l1.5-1.5zm-5 5a2 2 0 012.828 0 1 1 0 101.414-1.414 4 4 0 00-5.656 0l-3 3a4 4 0 105.656 5.656l1.5-1.5a1 1 0 10-1.414-1.414l-1.5 1.5a2 2 0 11-2.828-2.828l3-3z" clipRule="evenodd" />
    </svg>
);

// prettier-ignore
const unlinkSvg = (
    <svg className="tw-w-4 tw-h-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
);

/**
 * QuantityLinkCell renders the link button, linked indicator, and unlink button
 * for a single quantity slot on a template_child expense within a specific venue.
 *
 * Template quantity links are per-expense-slot-venue — each venue column can be
 * independently linked to a different source.
 */
function QuantityLinkCell({
    expenseId,
    expenseKey,
    quantityName,
    slotIndex,
    venueId,
    quantityLinkStates,
    editableTable,
    deferredLink,
    openDeferredQuantityPicker,
    clearDeferredQuantityLink,
}) {
    const [unlinking, setUnlinking] = useState(false);

    const linkEnabled = typeof window.quantityLinkEnabled !== 'undefined' && window.quantityLinkEnabled;

    // Unsaved template children have a synthetic "new-…" id (or none). They can't
    // be linked directly — the source is captured now and applied after save.
    const isDeferred = !expenseId || String(expenseId).indexOf('new-') === 0;

    const slotState = (() => {
        const venueStates = quantityLinkStates?.[expenseId]?.[venueId];
        if (venueStates && venueStates[slotIndex] !== undefined) {
            return venueStates[slotIndex];
        }
        return null;
    })();

    const isLinked = isDeferred ? linkEnabled && !!deferredLink : linkEnabled && slotState && slotState.source_type && !slotState.broken;
    const sourceLabel = isDeferred ? deferredLink?.label || '' : slotState?.meta?.label || '';
    const sourceUrl = isDeferred ? null : slotState?.source_url || null;

    const buildUrl = (template) => {
        if (!template) return null;
        return template.replace('EXPENSE_ID', expenseId);
    };

    const handleOpenPicker = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isDeferred) {
            openDeferredQuantityPicker?.({
                expenseKey,
                venueId,
                slotIndex,
                quantityName,
                current: deferredLink ? { sourceType: deferredLink.sourceType, sourceId: deferredLink.sourceId, driver: deferredLink.driver ?? null } : null,
            });
            return;
        }

        window.dispatchEvent(
            new CustomEvent('quantitylink:open', {
                detail: {
                    expenseId: expenseId,
                    sourcesUrl: buildUrl(window.quantityLinkSourcesUrlTemplate),
                    linkUrl: buildUrl(window.quantityLinkLinkUrlTemplate),
                    unlinkUrl: buildUrl(window.quantityLinkUnlinkUrlTemplate),
                    amountSlotIndex: slotIndex,
                    venueId: venueId,
                    currentLink: slotState
                        ? { sourceType: slotState.source_type, sourceId: slotState.source_id, driver: slotState.meta?.driver ?? null }
                        : null,
                },
            }),
        );
    };

    const handleUnlink = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (isDeferred) {
            clearDeferredQuantityLink?.({ expenseKey, venueId, slotIndex, quantityName });
            return;
        }

        if (!confirm($t('Are you sure you want to unlink this quantity? The current value will be frozen.'))) {
            return;
        }
        setUnlinking(true);
        const unlinkUrl = `${buildUrl(window.quantityLinkUnlinkUrlTemplate)}?slot_index=${slotIndex}&venue_id=${venueId}`;
        axios
            .delete(unlinkUrl)
            .then(() => {
                window.dispatchEvent(new CustomEvent('quantitylink:changed'));
            })
            .catch(() => {
                alert($t('Failed to unlink. Please try again.'));
            })
            .finally(() => setUnlinking(false));
    };

    if (!linkEnabled || !editableTable) {
        return null;
    }

    return (
        <div className="tw-flex tw-items-center tw-gap-1 tw-mt-1">
            {!isLinked && (
                <button
                    type="button"
                    className="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-md tw-bg-blue tw-text-white hover:tw-bg-blue-75 tw-transition-colors tw-shrink-0"
                    title={$t('Link quantity to an embedded source')}
                    aria-label={$t('Link quantity to an embedded source')}
                    onClick={handleOpenPicker}
                >
                    {linkSvg}
                </button>
            )}
            {isLinked && (
                <>
                    <span className="tw-flex tw-items-baseline tw-gap-1 tw-flex-1 tw-min-w-0" title={sourceLabel}>
                        <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-blue tw-shrink-0 tw-relative tw-top-px" />
                        <span className="tw-text-2xs tw-text-blue tw-break-words tw-min-w-0">
                            {$t('Linked to')}{' '}
                            {sourceUrl ? (
                                <a href={sourceUrl} className="tw-underline hover:tw-text-blue-75">
                                    {sourceLabel}
                                </a>
                            ) : (
                                <span className="tw-underline">{sourceLabel}</span>
                            )}
                        </span>
                    </span>
                    <button
                        type="button"
                        className="tw-inline-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-md tw-text-red-500 hover:tw-text-white hover:tw-bg-red-500 tw-transition-colors tw-shrink-0"
                        title={$t('Unlink quantity from source. The current value will be frozen.')}
                        aria-label={$t('Unlink quantity from source')}
                        onClick={handleUnlink}
                        disabled={unlinking}
                    >
                        {unlinkSvg}
                    </button>
                </>
            )}
        </div>
    );
}

export default function AssignTableVenues(props) {
    const [tooltipParentEl, setTooltipParentEl] = useState();

    const tableWrapperEl = useCallback((node) => {
        node && setTooltipParentEl(node);
    }, []);

    const containerRef = useRef();
    const gridRef = useRef();

    // Vertical scroll offset, used to drive the sticky category (divider) bands overlay.
    const [scrollTop, setScrollTop] = useState(0);
    const handleScroll = useCallback(({ scrollTop: top }) => setScrollTop(top), []);

    const linkEnabled = typeof window.quantityLinkEnabled !== 'undefined' && window.quantityLinkEnabled;

    const isSlotLinked = (expenseId, slotIndex, venueId) => {
        if (!linkEnabled) return false;
        const venueStates = props.quantityLinkStates?.[expenseId]?.[venueId];
        if (!venueStates) return false;
        const slotState = venueStates[slotIndex];
        return slotState && slotState.source_type && !slotState.broken;
    };

    // Pending link captured for an unsaved child (keyed by expense row index).
    const deferredLinkFor = (expenseKey, slotIndex, venueId) => props.deferredQuantityLinks?.[expenseKey]?.[venueId]?.[slotIndex] ?? null;
    const isSlotDeferredLinked = (expenseKey, slotIndex, venueId) => linkEnabled && !!deferredLinkFor(expenseKey, slotIndex, venueId);

    const showAnyTotals = props.showTotal || props.showUncommitted || props.showCommitted;

    // Build the ordered list of body rows (dividers + visible expense rows), each
    // carrying its ORIGINAL expenses index (risk R3 — never re-index after filtering).
    const visibleRows = useMemo(() => buildVisibleRows(props.expenses, props.showExpense), [props.expenses, props.showExpense]);

    const venues = props.venues;
    const columnCount = venues.length + 1;
    const rowCount = visibleRows.length + 1; // +1 fixed header row

    // ─── Measured row heights (CellMeasurer) ───
    // The header row (gridIndex 0) and every expense body row are measured to their exact
    // rendered height — no estimates, so no empty band below a row. A row's height is the MAX
    // over its cells: CellMeasurerCache.set derives rowHeight = max(getHeight(row, col)) across
    // columns, so the tall venue cell wins over the shorter fixed-left Description/Price/Total
    // cell. We MUST measure per-cell (the DEFAULT keyMapper). A keyMapper that collapses all
    // columns to one entry makes getHeight return the last-written value for every column, so the
    // short left cell overwrites the tall venue cell and the row TRUNCATES (the venue inputs get
    // clipped). Unmeasured (off-screen) columns contribute DEFAULT_MEASURED_HEIGHT to that max,
    // which is why it is kept LOW (below the shortest real row) — see the constant. fixedWidth:
    // true because every column is a known fixed width (height is what we measure).
    const cache = useMemo(
        () =>
            new CellMeasurerCache({
                defaultHeight: DEFAULT_MEASURED_HEIGHT,
                minHeight: MIN_MEASURED_HEIGHT,
                fixedWidth: true,
            }),
        [],
    );

    // Bumped after measurements settle (cells measure async on first render) to force the
    // divider overlay to recompute its offsets from the now-populated cache. Guarded below so
    // it never loops: it only changes when the measured body layout actually changes.
    const [measuredTick, setMeasuredTick] = useState(0);
    const lastLayoutKeyRef = useRef('');

    // grid rowIndex → kind. 0 is the fixed header (NOT a divider); body grid index i maps to
    // visibleRows[i - 1] (the fixedRowCount=1 header occupies grid index 0).
    const isDividerRow = useCallback((index) => index !== 0 && !!visibleRows[index - 1]?.isDivider, [visibleRows]);

    // Row height: divider rows are a uniform fixed strip; the header and expense rows come from
    // the measurement cache (defaultHeight until measured). This is the SINGLE source of truth —
    // the overlay derives its band offsets from the same function so the two never drift.
    const getRowHeight = useCallback(({ index }) => (isDividerRow(index) ? DIVIDER_ROW_HEIGHT : cache.rowHeight({ index })), [cache, isDividerRow]);

    // Expand the venue columns to fill the viewport when there are only a few, otherwise
    // keep the fixed minimum.
    // Known trade-off: after a window resize, MultiGrid's cached column widths go stale
    // until another geometry change triggers recomputeGridSize. AutoSizer re-renders the
    // component but does not invalidate the function-based columnWidth cache — an accepted
    // limitation. Cover in manual resize-while-scrolled check.
    const getVenueWidth = useCallback(() => {
        const available = containerRef.current?.clientWidth;
        if (!available || !venues.length) {
            return COLUMN_WIDTH;
        }
        const width = (available - COLUMN_WIDTH - 2) / venues.length;
        return Math.max(width, COLUMN_WIDTH);
    }, [venues.length]);

    const getColumnWidth = useCallback(({ index }) => (index === 0 ? COLUMN_WIDTH : getVenueWidth()), [getVenueWidth]);

    // Cumulative top offset of each body row, relative to the top of the scrolling body content
    // (the first body row sits at offset 0; the fixed header is applied separately via the
    // overlay container's `top`). Computed from the SAME heights the grid uses — measured
    // expense rows + fixed divider strips — so band positions match the rendered layout. Rows
    // not yet measured contribute defaultHeight, exactly as the grid reserves for them, so the
    // overlay stays consistent with the grid even before everything is measured. measuredTick is
    // an intentional dependency: it re-runs this memo once async measurements populate the cache
    // (the cache values are read imperatively, so the tick is what signals they changed).
    const bodyOffsets = useMemo(() => {
        const offsets = [];
        let acc = 0;
        for (let i = 0; i < visibleRows.length; i++) {
            offsets.push(acc);
            // body position i ↔ grid rowIndex i + 1 (header is grid row 0).
            acc += visibleRows[i].isDivider ? DIVIDER_ROW_HEIGHT : cache.rowHeight({ index: i + 1 });
        }
        return offsets;
    }, [visibleRows, cache, measuredTick]);

    // Measured header height, used to offset the overlay below the fixed header row. measuredTick
    // is an intentional dependency so it re-reads the cache once the header has measured.
    const headerHeight = useMemo(() => cache.rowHeight({ index: 0 }), [cache, measuredTick]);

    // Divider bands for the sticky overlay (one per visible divider row).
    const bands = useMemo(() => {
        const result = [];
        visibleRows.forEach((row, i) => {
            if (row.isDivider) {
                result.push({
                    key: `divider-${row.expenseIndex}`,
                    expense: row.expense,
                    expenseIndex: row.expenseIndex,
                    expanded: props.showExpense[row.expenseIndex + 1],
                    naturalTop: bodyOffsets[i],
                    height: DIVIDER_ROW_HEIGHT,
                });
            }
        });
        return result;
    }, [visibleRows, bodyOffsets, props.showExpense]);

    // Invalidate measured heights + recompute grid geometry ONLY when content HEIGHT can change:
    // the three show* totals toggles, the number of venue columns, and the visibleRows STRUCTURE
    // (collapse/expand, add/remove). Cell EDITS change values, not heights, so they must NOT land
    // here — clearing on every keystroke would re-measure constantly (perf + focus risk). The
    // structure key changes whenever a row is added/removed/reordered or a divider toggles.
    const structureKey = useMemo(() => visibleRows.map((r) => `${r.expenseIndex}:${r.isDivider ? 'd' : 'e'}`).join('|'), [visibleRows]);

    useEffect(() => {
        cache.clearAll();
        // Force the overlay to recompute from the (now-cleared) cache too.
        lastLayoutKeyRef.current = '';
        if (gridRef.current) {
            gridRef.current.recomputeGridSize();
        }
    }, [cache, structureKey, venues.length, props.showTotal, props.showUncommitted, props.showCommitted]);

    // Cells measure asynchronously on first render and after each clearAll. Once a batch of
    // cells has rendered, re-derive the overlay offsets from the freshly-populated cache.
    // GUARD against an infinite setState loop: only bump the tick when the layout the overlay
    // depends on (header height + every divider band's cumulative top + the running total)
    // actually changed since the last bump. When nothing changed, this is a no-op and React
    // does not re-render. The string key folds in only the divider offsets the overlay reads.
    const handleSectionRendered = useCallback(() => {
        let acc = cache.rowHeight({ index: 0 }); // header
        const parts = [acc];
        for (let i = 0; i < visibleRows.length; i++) {
            if (visibleRows[i].isDivider) {
                parts.push(acc);
            }
            acc += visibleRows[i].isDivider ? DIVIDER_ROW_HEIGHT : cache.rowHeight({ index: i + 1 });
        }
        parts.push(acc); // running total (last band's anchoring depends on it)
        const layoutKey = parts.join(',');
        if (layoutKey !== lastLayoutKeyRef.current) {
            lastLayoutKeyRef.current = layoutKey;
            setMeasuredTick((t) => t + 1);
        }
    }, [cache, visibleRows]);

    const renderHeaderParentCell = (measure, style) => (
        <MeasuredCell
            key={measure.key}
            cache={cache}
            measure={measure}
            style={style}
            className="tw-flex tw-flex-col tw-p-1.5 tw-bg-[#C1D7FD] tw-border-r-2 tw-border-grey"
        >
            <div className={`tw-grid tw-gap-4 ${showAnyTotals ? 'tw-grid-cols-4 tw-mb-4' : 'tw-grid-cols-3'}`}>
                {showAnyTotals && <div></div>}

                <div className="tw-col-span-3">
                    <div className="tw-flex tw-justify-center tw-gap-x-2">
                        <a className="tw-text-blue hover:tw-text-blue-75" href="#">
                            {props.parentName}
                        </a>
                        <button onClick={(e) => props.handleRotate()}>
                            <ArrowPathIcon className="tw-w-2.5 tw-h-2.5" />
                        </button>
                    </div>
                </div>
            </div>
            {showAnyTotals && (
                <>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4">
                        <div></div>
                        {props.showTotal && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Budget')}</div>
                            </div>
                        )}
                        {props.showUncommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Uncommitted')}</div>
                            </div>
                        )}
                        {props.showCommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Committed')}</div>
                            </div>
                        )}
                    </div>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4">
                        <div>{$t('Total')}:</div>
                        {props.showTotal && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.budget.grand, formatAndRoundMoney)}</span>
                            </div>
                        )}
                        {props.showUncommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.uncommitted.grand, formatAndRoundMoney)}</span>
                            </div>
                        )}
                        {props.showCommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.committed.grand, formatAndRoundMoney)}</span>
                            </div>
                        )}
                    </div>
                </>
            )}
        </MeasuredCell>
    );

    const renderHeaderVenueCell = (measure, style, venue) => (
        <MeasuredCell
            key={measure.key}
            cache={cache}
            measure={measure}
            style={style}
            className="tw-flex tw-flex-col tw-p-1.5 tw-bg-[#C1D7FD] tw-border-r-2 tw-border-grey"
        >
            <div className="tw-flex tw-flex-col tw-gap-y-4 tw-justify-center tw-items-center">
                <div className="tw-flex tw-flex-row tw-justify-center tw-items-center tw-gap-x-2 tw-w-full">
                    {props.selectable && props.editableTable && !props.finalStates.venues[venue.id].isDisabled && (
                        <CheckboxInput
                            id={venue.id}
                            checked={props.finalStates.venues[venue.id].isSelected}
                            label=""
                            onChange={() => props.toggleFinalState('parentVenue', venue.id)}
                        />
                    )}
                    <Tooltip
                        placement="right"
                        tooltipType="content"
                        backgroundClass="tw-bg-white"
                        tooltipContent={<VenueTooltip venue={venue} />}
                        parentEl={tooltipParentEl}
                    >
                        {window.profitLossUrl ? (
                            <a className="tw-text-blue hover:tw-text-blue-75" href={window.profitLossUrl.replace('VENUE_ID', venue.slug)}>
                                {venue.name}
                            </a>
                        ) : (
                            <div>{venue.name}</div>
                        )}
                    </Tooltip>
                </div>
                {showAnyTotals && (
                    <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full">
                        {props.showTotal && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                <div>{$t('Budget')}</div>
                                <span className="tw-break-all">
                                    {formatFigure(
                                        Object.prototype.hasOwnProperty.call(props.totals.venues, venue.id) ? props.totals.venues[venue.id].budget : '0',
                                        formatAndRoundMoney,
                                    )}
                                </span>
                            </div>
                        )}
                        {props.showUncommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                <div>{$t('Uncommitted')}</div>
                                <span className="tw-break-all">
                                    {formatFigure(
                                        Object.prototype.hasOwnProperty.call(props.totals.venues, venue.id) ? props.totals.venues[venue.id].uncommitted : '0',
                                        formatAndRoundMoney,
                                    )}
                                </span>
                            </div>
                        )}
                        {props.showCommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                <div>{$t('Committed')}</div>
                                <span className="tw-break-all">
                                    {formatFigure(
                                        Object.prototype.hasOwnProperty.call(props.totals.venues, venue.id) ? props.totals.venues[venue.id].committed : '0',
                                        formatAndRoundMoney,
                                    )}
                                </span>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </MeasuredCell>
    );

    const renderExpenseFixedCell = (measure, style, expense, expenseIndex) => (
        <MeasuredCell
            key={measure.key}
            cache={cache}
            measure={measure}
            style={style}
            className="tw-flex tw-flex-col tw-p-1.5 tw-bg-[#f1f1f1] tw-border-r-2 tw-border-t-2 tw-border-grey"
        >
            <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-w-full tw-mb-4">
                <div className="tw-font-bold">{$t('Description')}:</div>
                <div className="tw-flex tw-flex-row tw-items-center tw-col-span-3">
                    {props.selectable && props.editableTable && !props.forProcurement && !props.finalStates.expenses[expense.id].isDisabled && (
                        <CheckboxInput
                            id={expense.id}
                            checked={props.finalStates.expenses[expense.id].isSelected}
                            label=""
                            onChange={() => props.toggleFinalState('expense', null, expense.id)}
                        />
                    )}
                    <div
                        className={`tw-text-blue hover:tw-text-blue-75 tw-font-semibold ${props.selectable && props.editableTable && !props.finalStates.expenses[expense.id].isDisabled ? 'tw-pl-2' : ''}`}
                    >
                        {expense.name}
                    </div>
                </div>
            </div>
            <div className={`tw-grid tw-grid-cols-4 tw-gap-4 tw-w-full ${showAnyTotals && 'tw-mb-4'}`}>
                <div className="tw-font-bold">{$t('Price')}:</div>
                <div className="tw-flex tw-flex-row tw-col-span-3 tw-items-center tw-mb-1">
                    {formatFigure(expense.price, formatSeparateByThousands, { precision: 3 })}
                </div>
            </div>
            {showAnyTotals && (
                <>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-w-full">
                        <div></div>
                        {props.showTotal && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Budget')}</div>
                            </div>
                        )}
                        {props.showUncommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Uncommitted')}</div>
                            </div>
                        )}
                        {props.showCommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-mb-1">
                                <div>{$t('Committed')}</div>
                            </div>
                        )}
                    </div>
                    <div className="tw-grid tw-grid-cols-4 tw-gap-4 tw-w-full">
                        <div className="tw-font-bold">{$t('Total')}:</div>
                        {props.showTotal && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.expenses[expenseIndex].budget.total, formatAndRoundMoney)}</span>
                            </div>
                        )}
                        {props.showUncommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.expenses[expenseIndex].uncommitted.total, formatAndRoundMoney)}</span>
                            </div>
                        )}
                        {props.showCommitted && (
                            <div className="tw-flex tw-flex-col tw-justify-between tw-items-center">
                                <span className="tw-break-all">{formatFigure(props.totals.expenses[expenseIndex].committed.total, formatAndRoundMoney)}</span>
                            </div>
                        )}
                    </div>
                </>
            )}
        </MeasuredCell>
    );

    const renderVenueDataCell = (measure, style, expense, expenseIndex, venue) => {
        let assignedValues = JSON.parse('{}');
        let expenseableId = null;
        const selectedVenue = typeof expense.venues.find((v) => v.id == venue.id) !== 'undefined' ? expense.venues.find((v) => v.id == venue.id) : null;
        if (selectedVenue) {
            const pivot = typeof selectedVenue.pivot !== 'undefined' ? selectedVenue.pivot : null;
            if (pivot) {
                assignedValues = JSON.parse(typeof pivot.uncommitted_amount !== 'undefined' ? pivot.uncommitted_amount : '{}');
                expenseableId = typeof pivot.id !== 'undefined' ? pivot.id : null;
            }
        }

        return (
            <MeasuredCell
                key={measure.key}
                cache={cache}
                measure={measure}
                style={style}
                className="tw-p-1.5 tw-bg-[#f1f1f1] tw-border-r-2 tw-border-t-2 tw-border-grey"
            >
                <div className="tw-space-y-2">
                    {props.selectable &&
                        props.editableTable &&
                        props.finalStates.venues[venue.id].isSelected &&
                        props.finalStates.expenses[expense.id].isSelected &&
                        Object.prototype.hasOwnProperty.call(props.finalStates.expenses[expense.id].venues, venue.id) &&
                        !props.forProcurement &&
                        !props.finalStates.expenses[expense.id].venues[venue.id]?.isDisabled && (
                            <CheckboxInput
                                id={expense.id}
                                checked={props.finalStates.expenses[expense.id].venues[venue.id].isSelected}
                                label=""
                                onChange={() => props.toggleFinalState('venue', venue.id, expense.id)}
                            />
                        )}
                    {!!expense.amount.length &&
                        expense.amount.map(
                            (quantity, i) =>
                                quantity.name && (
                                    <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full" key={i}>
                                        <div className="tw-col-span-3 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
                                            <label className="text-input__label tw-text-xs">{quantity.name}</label>
                                            {props.selectable ? (
                                                <FloatInput
                                                    containerClasses="tw-w-full"
                                                    variant="white"
                                                    value={
                                                        Object.prototype.hasOwnProperty.call(assignedValues, quantity.name) ? assignedValues[quantity.name] : 0
                                                    }
                                                    prefix="#"
                                                    expenseKey={expenseIndex}
                                                    venueId={venue.id}
                                                    quantityName={quantity.name}
                                                    price={expense.price}
                                                    handleQuantityChange={props.handleQuantityChange}
                                                    isLinked={isSlotLinked(expense.id, i, venue.id) || isSlotDeferredLinked(expenseIndex, i, venue.id)}
                                                    disabled={
                                                        !props.editableTable ||
                                                        isSlotLinked(expense.id, i, venue.id) ||
                                                        isSlotDeferredLinked(expenseIndex, i, venue.id) ||
                                                        props.finalStates.venues[venue.id].isDisabled ||
                                                        props.finalStates.expenses[expense.id].isDisabled ||
                                                        props.finalStates.expenses[expense.id].venues[venue.id]?.isDisabled ||
                                                        !props.finalStates.venues[venue.id].isSelected ||
                                                        !props.finalStates.expenses[expense.id].isSelected ||
                                                        !props.finalStates.expenses[expense.id].venues[venue.id]?.isSelected ||
                                                        props.forProcurement
                                                    }
                                                />
                                            ) : (
                                                <FloatInput
                                                    containerClasses="tw-w-full"
                                                    variant="white"
                                                    value={
                                                        Object.prototype.hasOwnProperty.call(assignedValues, quantity.name) ? assignedValues[quantity.name] : 0
                                                    }
                                                    prefix="#"
                                                    expenseKey={expenseIndex}
                                                    venueId={venue.id}
                                                    quantityName={quantity.name}
                                                    price={expense.price}
                                                    handleQuantityChange={props.handleQuantityChange}
                                                    isLinked={isSlotLinked(expense.id, i, venue.id) || isSlotDeferredLinked(expenseIndex, i, venue.id)}
                                                    disabled={
                                                        !props.editableTable ||
                                                        isSlotLinked(expense.id, i, venue.id) ||
                                                        isSlotDeferredLinked(expenseIndex, i, venue.id) ||
                                                        props.forProcurement
                                                    }
                                                />
                                            )}
                                            <QuantityLinkCell
                                                expenseId={expense.id}
                                                expenseKey={expenseIndex}
                                                quantityName={quantity.name}
                                                slotIndex={i}
                                                venueId={venue.id}
                                                quantityLinkStates={props.quantityLinkStates}
                                                editableTable={props.editableTable}
                                                deferredLink={deferredLinkFor(expenseIndex, i, venue.id)}
                                                openDeferredQuantityPicker={props.openDeferredQuantityPicker}
                                                clearDeferredQuantityLink={props.clearDeferredQuantityLink}
                                            />
                                        </div>
                                    </div>
                                ),
                        )}
                    <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full">
                        {Object.prototype.hasOwnProperty.call(assignedValues, 'Price') ? (
                            <div className="tw-col-span-3 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
                                <label className="text-input__label tw-text-xs">{$t('Price')}</label>
                                {props.selectable ? (
                                    <FloatInput
                                        containerClasses="tw-w-full"
                                        variant="white"
                                        value={assignedValues.Price}
                                        expenseKey={expenseIndex}
                                        venueId={venue.id}
                                        quantityName={'Price'}
                                        price={expense.price}
                                        handleQuantityChange={props.handleQuantityChange}
                                        thousandSeparator={true}
                                        disabled={
                                            !props.editableTable ||
                                            props.finalStates.venues[venue.id].isDisabled ||
                                            props.finalStates.expenses[expense.id].isDisabled ||
                                            props.finalStates.expenses[expense.id].venues[venue.id]?.isDisabled ||
                                            !props.finalStates.venues[venue.id].isSelected ||
                                            !props.finalStates.expenses[expense.id].isSelected ||
                                            !props.finalStates.expenses[expense.id].venues[venue.id]?.isSelected ||
                                            props.forProcurement
                                        }
                                        suffix={
                                            props.editableTable && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => props.handleQuantityChange(false, expenseIndex, venue.id, 'Price', expense.price)}
                                                >
                                                    <TrashIcon className="tw-w-3 tw-h-3 tw-text-red" />
                                                </button>
                                            )
                                        }
                                    />
                                ) : (
                                    <FloatInput
                                        containerClasses="tw-w-full"
                                        variant="white"
                                        value={assignedValues.Price}
                                        expenseKey={expenseIndex}
                                        venueId={venue.id}
                                        quantityName={'Price'}
                                        price={expense.price}
                                        handleQuantityChange={props.handleQuantityChange}
                                        thousandSeparator={true}
                                        disabled={!props.editableTable || props.forProcurement}
                                        suffix={
                                            props.editableTable && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => props.handleQuantityChange(false, expenseIndex, venue.id, 'Price', expense.price)}
                                                >
                                                    <TrashIcon className="tw-w-3 tw-h-3 tw-text-red" />
                                                </button>
                                            )
                                        }
                                    />
                                )}
                            </div>
                        ) : props.editableTable && !props.forProcurement ? (
                            <div className="tw-col-span-3 tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-y-1">
                                {props.selectable ? (
                                    <TextButton
                                        variant="primary"
                                        onClick={(e) =>
                                            props.handleQuantityChange(
                                                expense.price === 0 ? '0' : expense.price,
                                                expenseIndex,
                                                venue.id,
                                                'Price',
                                                expense.price,
                                            )
                                        }
                                        disabled={
                                            props.finalStates.venues[venue.id].isDisabled ||
                                            props.finalStates.expenses[expense.id].isDisabled ||
                                            props.finalStates.expenses[expense.id].venues[venue.id]?.isDisabled ||
                                            !props.finalStates.venues[venue.id].isSelected ||
                                            !props.finalStates.expenses[expense.id].isSelected ||
                                            !props.finalStates.expenses[expense.id].venues[venue.id]?.isSelected
                                        }
                                    >
                                        {$t('Set custom price')}
                                    </TextButton>
                                ) : (
                                    <TextButton
                                        variant="primary"
                                        onClick={(e) =>
                                            props.handleQuantityChange(
                                                expense.price === 0 ? '0' : expense.price,
                                                expenseIndex,
                                                venue.id,
                                                'Price',
                                                expense.price,
                                            )
                                        }
                                    >
                                        {$t('Set custom price')}
                                    </TextButton>
                                )}
                            </div>
                        ) : (
                            <div className="tw-col-span-3 tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                <label className="text-input__label tw-text-xs">{$t('Price')}</label>
                                <div className="tw-flex tw-items-center tw-h-7 tw-rounded-lg tw-text-xs tw-px-2 tw-py-1 tw-bg-white tw-text-charcoal tw-font-semibold">
                                    {formatFigure(expense.price)}
                                </div>
                            </div>
                        )}
                    </div>
                    {showAnyTotals && (
                        <div className="tw-grid tw-grid-cols-3 tw-gap-4 tw-w-full">
                            {props.showTotal && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                    <div>{$t('Budget')}</div>
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.expenses[expenseIndex].venues, venue.id)
                                                ? props.totals.expenses[expenseIndex].venues[venue.id].budget.total
                                                : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                            {props.showUncommitted && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                    <div>{$t('Uncommitted')}</div>
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.expenses[expenseIndex].venues, venue.id)
                                                ? props.totals.expenses[expenseIndex].venues[venue.id].uncommitted.total
                                                : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                            {props.showCommitted && (
                                <div className="tw-flex tw-flex-col tw-justify-between tw-items-center tw-gap-y-1">
                                    <div className="tw-flex tw-gap-x-2 tw-items-center">
                                        <div>{$t('Committed')}</div>
                                        {props.showCommitted &&
                                            Object.prototype.hasOwnProperty.call(props.totals.expenses[expenseIndex].venues, venue.id) &&
                                            props.totals.expenses[expenseIndex].venues[venue.id].committed.total > 0 && (
                                                <div
                                                    className="tw-cursor-pointer"
                                                    onClick={(e) => props.showCommitmentModal(expense.committed_expenditures, venue.id, expenseableId)}
                                                >
                                                    <HelpCircleIcon></HelpCircleIcon>
                                                </div>
                                            )}
                                    </div>
                                    <span className="tw-break-all">
                                        {formatFigure(
                                            Object.prototype.hasOwnProperty.call(props.totals.expenses[expenseIndex].venues, venue.id)
                                                ? props.totals.expenses[expenseIndex].venues[venue.id].committed.total
                                                : '0',
                                            formatAndRoundMoney,
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </MeasuredCell>
        );
    };

    // MultiGrid passes `parent` (the Grid instance) so CellMeasurer can register/invalidate.
    // We forward {cache columnIndex parent rowIndex key} to each measured renderer via `measure`.
    const cellRenderer = useCallback(
        ({ columnIndex, rowIndex, key, style, parent }) => {
            const measure = { columnIndex, rowIndex, key, parent };

            // ─── Fixed header row (measured) ───
            if (rowIndex === 0) {
                if (columnIndex === 0) {
                    return renderHeaderParentCell(measure, style);
                }
                const venue = venues[columnIndex - 1];
                return renderHeaderVenueCell(measure, style, venue);
            }

            const row = visibleRows[rowIndex - 1];
            if (!row) {
                return <div key={key} style={style} />;
            }

            // ─── Divider rows: FIXED-height empty strips (NOT measured); the visible charcoal
            //     band is drawn by the overlay. Uniform DIVIDER_ROW_HEIGHT, so no CellMeasurer. ───
            if (row.isDivider) {
                return <div key={key} style={style} className="tw-bg-charcoal-50 tw-overflow-hidden" />;
            }

            // ─── Expense rows (measured) ───
            if (columnIndex === 0) {
                return renderExpenseFixedCell(measure, style, row.expense, row.expenseIndex);
            }
            const venue = venues[columnIndex - 1];
            return renderVenueDataCell(measure, style, row.expense, row.expenseIndex, venue);
        },
        [
            visibleRows,
            venues,
            props.totals,
            props.finalStates,
            props.showExpense,
            props.showTotal,
            props.showUncommitted,
            props.showCommitted,
            props.quantityLinkStates,
            props.deferredQuantityLinks,
            props.selectable,
            props.editableTable,
            props.forProcurement,
            tooltipParentEl,
        ],
    );

    return (
        // Height strategy: this grid renders full-page AND inside two modals
        // (ProcurementExpenseEditModal / UncommittedExpendituresEditModal). The modal body
        // (.modal__inner-container) is NOT independently scrollable — scroll happens on the
        // outer .modal__box (overflow-y-auto + max-h-full). So we cap to the available space
        // (tw-max-h-full) and let the modal scroll any overflow, rather than forcing a hard
        // 72vh box that could spill past the modal and clip under !tw-overflow-hidden. The
        // min-height keeps the virtualised grid usable on short viewports.
        <div ref={tableWrapperEl} className="inbud-table_wrapper !tw-overflow-hidden tw-h-[72vh] tw-max-h-full tw-min-h-[24rem]">
            <div ref={containerRef} className="tw-relative tw-h-full tw-w-full">
                <AutoSizer>
                    {({ width, height }) => (
                        <div className="tw-relative" style={{ width, height }}>
                            <MultiGrid
                                ref={gridRef}
                                width={width}
                                height={height}
                                onScroll={handleScroll}
                                onSectionRendered={handleSectionRendered}
                                deferredMeasurementCache={cache}
                                rowHeight={getRowHeight}
                                rowCount={rowCount}
                                columnWidth={getColumnWidth}
                                columnCount={columnCount}
                                fixedColumnCount={1}
                                fixedRowCount={1}
                                cellRenderer={cellRenderer}
                                styleTopRightGrid={{ backgroundColor: '#C1D7FD' }}
                                styleTopLeftGrid={{ backgroundColor: '#C1D7FD' }}
                                // Data "busting" props: change identity whenever cell CONTENT (values)
                                // changes so MultiGrid re-renders cells. These do NOT clear the height
                                // cache — edits change values, not heights (see the clearAll effect).
                                visibleRows={visibleRows}
                                expenses={props.expenses}
                                totals={props.totals}
                                finalStates={props.finalStates}
                                quantityLinkStates={props.quantityLinkStates}
                                deferredQuantityLinks={props.deferredQuantityLinks}
                                showTotal={props.showTotal}
                                showUncommitted={props.showUncommitted}
                                showCommitted={props.showCommitted}
                                measuredTick={measuredTick}
                            />

                            <DividerBandsOverlay
                                bands={bands}
                                venues={venues}
                                scrollTop={scrollTop}
                                headerHeight={headerHeight}
                                viewportHeight={height}
                                getColumnWidth={getColumnWidth}
                                toggleVisibility={props.toggleVisibility}
                            />
                        </div>
                    )}
                </AutoSizer>
            </div>
        </div>
    );
}

// Wraps a header / expense cell in a CellMeasurer so MultiGrid measures its rendered height.
// Module-scope (stable component identity) so the cell subtree is NOT remounted on every parent
// render — remounting would lose FloatInput focus and force a re-measure on each keystroke.
// CRITICAL: the measured element must NOT be height-constrained — we spread MultiGrid's `style`
// for absolute top/left/width but FORCE `height: 'auto'` so the cell grows to its content during
// measurement. We also keep `tw-overflow-hidden` OFF measured cells: with an exact measured
// height nothing needs clipping, and overflow-hidden would stop the cell growing while measuring.
// The React reconciliation `key` is set on the <MeasuredCell> element by each caller (the cell
// identity in MultiGrid's rendered array); the `registerChild` ref MUST be on the measured DOM
// node so CellMeasurer measures the right element.
const MeasuredCell = ({ cache, measure, className, style, children }) => (
    <CellMeasurer cache={cache} columnIndex={measure.columnIndex} parent={measure.parent} rowIndex={measure.rowIndex}>
        {({ registerChild }) => (
            // height:auto lets the cell grow to its content so CellMeasurer can measure it.
            // minHeight pins it to the row height (style.height = the measured MAX across the row's
            // cells, which MultiGrid passes to every cell in the row), so a cell SHORTER than the row
            // — e.g. the fixed-left Description/Price/Total column — fills the row with its background
            // instead of leaving an empty gap below it. This converges: the row height equals the
            // TALLEST cell's content; shorter cells only measure up to that height and stop (bounded
            // by the tallest cell), so the height never grows unbounded.
            <div ref={registerChild} style={{ ...style, height: 'auto', minHeight: style.height }} className={className}>
                {children}
            </div>
        )}
    </CellMeasurer>
);

// ─── Sticky divider (category group) bands overlay ───
// Draws each divider as a full-width charcoal band over the grid body. The band whose
// row currently sits at the top of the viewport pins just below the venue header row
// and is pushed up by the next band as it scrolls in.
const DividerBandsOverlay = ({ bands, venues, scrollTop, headerHeight, viewportHeight, getColumnWidth, toggleVisibility }) => {
    if (!bands.length) return null;

    const bodyHeight = viewportHeight - headerHeight;
    const fixedWidth = getColumnWidth({ index: 0 });

    let activeIndex = -1;
    for (let i = 0; i < bands.length; i++) {
        if (bands[i].naturalTop <= scrollTop) activeIndex = i;
        else break;
    }

    return (
        <div className="tw-absolute tw-inset-x-0 tw-overflow-hidden tw-pointer-events-none" style={{ top: headerHeight, height: bodyHeight }}>
            {bands.map((band, i) => {
                const rawTop = band.naturalTop - scrollTop;

                if (i < activeIndex) return null;
                if (rawTop > bodyHeight) return null;

                let top = rawTop;
                let pinned = false;
                if (i === activeIndex) {
                    pinned = true;
                    top = 0;
                    const next = bands[i + 1];
                    if (next) {
                        const nextRaw = next.naturalTop - scrollTop;
                        if (nextRaw < band.height) top = nextRaw - band.height;
                    }
                }

                return (
                    <DividerBandCell
                        key={band.key}
                        expense={band.expense}
                        expanded={band.expanded}
                        venuesCount={venues.length}
                        fixedWidth={fixedWidth}
                        pinned={pinned}
                        onToggle={() => toggleVisibility(band.expense)}
                        style={{ top, height: band.height }}
                    />
                );
            })}
        </div>
    );
};

// ─── Divider band (charcoal bg, name + collapse/expand chevron on the fixed-left strip) ───
const DividerBandCell = ({ expense, expanded, venuesCount, fixedWidth, pinned, onToggle, style }) => (
    // The overlay container is pointer-events-none; re-enable pointer events on the band itself
    // and make the WHOLE band clickable to toggle (matching the original divider row), not just
    // the left label strip — so the hit target spans the filler over the venue columns too.
    <div
        style={{ ...style, pointerEvents: 'auto' }}
        className={`tw-absolute tw-inset-x-0 tw-flex tw-cursor-pointer !tw-bg-charcoal-50 ${pinned ? 'tw-z-20 tw-shadow-md' : 'tw-z-10'}`}
        onClick={onToggle}
    >
        <div className="tw-flex tw-items-center tw-gap-x-2 tw-px-1.5 tw-text-white tw-shrink-0" style={{ width: fixedWidth }}>
            <div className="tw-font-medium">{expense.name}</div>
            <div>{expanded ? <ChevronUpIcon className="tw-w-2.5 tw-h-2.5" /> : <ChevronDownIcon className="tw-w-2.5 tw-h-2.5" />}</div>
        </div>
        {!!venuesCount && <div className="tw-flex-1" />}
    </div>
);
